import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { setToken } from '@/Config/auth/authStore';
import { trackAndNavigate } from '@/Config/libs/analytics';
import { PostHogEventEnum, PostHogFeatureFlagEnum } from '@/Domain/enum/PostHogEvent.enum';
import { useCaptchaFallback } from '@/Domain/hooks/Auth/useCaptchaFallback.hook';
import errorMapper, { Error, ErrorCodes } from '@/Domain/utils/errorMapper';
import {
  clearPendingVerificationEmail,
  setPendingVerificationEmail,
} from '@/Domain/utils/pendingVerificationEmail';
import { readNextParamFromSearch } from '@/Domain/utils/safeNextPath';

const LoginValidator = z.object({
  username: z.string().email(),
  password: z.string().min(1, i18n.t('validation:password_required')),
  recaptcha_response_v3: z.string().optional(),
  recaptcha_response_v2: z.string().optional(),
});

export type LoginForm = z.infer<typeof LoginValidator>;

export const useLogin = () => {
  const { t } = useTranslation('validation');
  const { track } = useAnalytics();
  const postHog = usePostHog();
  const navigate = useNavigate();

  const [isPending, setIsPending] = useState(false);
  const [tooManyRequests, setTooManyRequests] = useState<boolean>(false);
  const [showGoogleHint, setShowGoogleHint] = useState<boolean>(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    setValue,
    register,
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginValidator),
  });

  register('recaptcha_response_v3');

  const {
    handleCaptchaError,
    handleSubmissionError,
    handleV2Focus,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  } = useCaptchaFallback({
    clearSubmissionErrors: () => {
      clearErrors('root');
      clearErrors('recaptcha_response_v2');
    },
    hasV2Token: () => !!getValues('recaptcha_response_v2'),
    resetV2Token: () => setValue('recaptcha_response_v2', ''),
    setGenericError: () => setError('root', { message: i18n.t('login:error_generic') }),
    setRequiredV2Error: () => setError('recaptcha_response_v2', { type: 'required' }),
  });

  const mutation = useMutation<
    { email: string; user_id: number; jwt_token: string; default_scenario_id: string | null },
    Error<LoginForm>,
    LoginForm
  >({
    mutationKey: [QueryKey.accounts.login],
    throwOnError: false,
    onSuccess: (response) => {
      clearPendingVerificationEmail();

      // Store JWT from login response in auth store
      if (response.jwt_token) {
        setToken(response.jwt_token);
      }

      // Identify user in PostHog so this event and future events are linked to
      // the user. Keyed on the stable numeric user_id to preserve continuity
      // with anonymous→identified history captured pre-SPA migration.
      postHog.identify(response.user_id.toString());

      // Get the A/B test variant for tracking
      const backgroundVariant = postHog.getFeatureFlag(PostHogFeatureFlagEnum.SignupBackgroundTest);

      const scenarioId = response.default_scenario_id;
      const nextPresent = new URLSearchParams(window.location.search).get('next') !== null;
      // The logged-out MCP-authorize redirect lands here as a URL `next`; replay it.
      const nextPath = readNextParamFromSearch(window.location.search);
      const navigateTo = nextPath ?? (scenarioId ? `/i/${scenarioId}/` : '/');

      // Experiment analysis + MCP logged-out-resume visibility. next_present=URL had `next`;
      // next_replayed=we navigated to it (present && !replayed = rejected by getSafeNextPath).
      postHog.capture(PostHogEventEnum.LoginCompleted, {
        background_variant: backgroundVariant,
        next_present: nextPresent,
        next_replayed: nextPath !== null,
      });

      trackAndNavigate(
        'login_form_success',
        {
          event_category: 'User login',
          event_label: 'login_form',
          background_variant: backgroundVariant ?? 'unknown',
        },
        () => {
          // Full document navigation: the entry shell (login route) is a
          // separate router tree from the operator-app routes, so we always
          // reload to remount the SPA root with the post-login state.
          window.location.href = navigateTo;
        },
      );
    },
    onError: (error) => {
      handleSubmissionError();
      setIsPending(false);
      track('login_form_error', { event_category: 'User login', event_label: 'login_form' });

      const fieldErrors = error.response?.data?.errors;
      if (
        (error.response?.status ?? 0) >= 500 ||
        !fieldErrors ||
        !Object.keys(fieldErrors).length
      ) {
        setError('root', { message: i18n.t('login:error_generic') });
        return;
      }

      // eslint-disable-next-line
      errorMapper(fieldErrors, (field, message, code, options) => {
        if (handleCaptchaError(field)) {
          return;
        }

        if (code && code === ErrorCodes.TOO_MANY_REQUESTS) {
          setTooManyRequests(true);
          return;
        }

        if (code === ErrorCodes.NOT_ACTIVATED) {
          const userEmail = options?.user_email;
          if (userEmail) {
            setPendingVerificationEmail(userEmail);
          } else {
            // Drop any address left by an earlier signup so resend can't target the wrong account.
            clearPendingVerificationEmail();
          }
          navigate('/register/verify', { viewTransition: true });
          return;
        }

        if (code === ErrorCodes.USE_GOOGLE_SIGNIN) {
          setShowGoogleHint(true);
          return;
        }

        setError('username', { message: t('invalid_credentials') });
        setError('password', { message: t('invalid_credentials') });
      });
    },
  });

  const onSubmit = (data: LoginForm) => {
    const payload = prepareCaptchaSubmission(data);
    if (!payload) {
      return;
    }

    track('login_form_submit', { event_category: 'User login', event_label: 'login_form' });
    setIsPending(true);
    setTooManyRequests(false);
    setShowGoogleHint(false);
    return mutation.mutate(payload);
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [mutation.submittedAt],
  );

  return {
    control,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    register,
    errors,
    handleSubmit,
    onSubmit,
    onVerify,
    isPending,
    tooManyRequests,
    showGoogleHint,
  };
};
